import { useMemo } from 'react'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import OpenInNew from '@mui/icons-material/OpenInNew'
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useUsersById from '@/hooks/useUsersById'
import { useGetApprovalPolicies } from '@/service-library/hooks/approval-policies'
import { useGetPositions } from '@/service-library/hooks/positions'
import { ApprovalPolicy, ApprovalStatePolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import { WorkflowStateAction } from '@/types/workflow-state-actions'
import { orderedUserIds, policyApproverRows } from '@/utils/approver-entries'
import ApprovalPolicyDetailsDialog from '@/components/approvals/ApprovalPolicyDetailsDialog'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

const ALL_POLICIES_TEXT =
  'PixyAI will check the document against every enabled approval policy on this project and will apply each one that matches.'

const NAMED_POLICIES_TEXT =
  'PixyAI will check the document against these approval policies and will apply each one that matches.'

const MOVE_ON_TEXT =
  'If nothing matches, the document moves on without approval.'

/**
 * The details dialog expects a policy's progress on a document. A workflow step
 * has no document, so every approver in it reads as undecided.
 */
function asUndecidedState(policy: ApprovalPolicy): ApprovalStatePolicy {
  return {
    approval_policy_id: policy.id,
    description: policy.description,
    approval_policy_version_id: policy.current_version_id,
    // Pinning happens when someone approves or rejects a document. There is no
    // document here, so nothing is pinned.
    pinned_approval_policy_version_id: null,
    approval_order: policy.approval_order,
    approvals_required: policy.approvals_required,
    config_resolved: true,
    approvals_received: 0,
    satisfied: false,
    is_rejected: false,
    next_required_user_id: null,
    next_required: null,
    // Only the count is read from here — the dialog is handed the real rows,
    // which can name a position this shape has no room for.
    approvers: policy.approvers.map(({ sort_order }) => ({
      sort_order,
      kind: 'user',
      user_id: '',
      latest_action: null,
    })),
  }
}

type PolicyRowProps = {
  policy: ApprovalPolicy
  usersById: Map<string, AuthUser>
  positionNamesById: Map<string, string>
  positionsPending: boolean
  usersPending: boolean
}

function PolicyRow({
  policy,
  usersById,
  positionNamesById,
  positionsPending,
  usersPending,
}: PolicyRowProps) {
  const detailsOverlay = useOverlay()
  const statePolicy = useMemo(() => asUndecidedState(policy), [policy])

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        py: 0.75,
        px: 1.25,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          noWrap
          sx={{ minWidth: 0, fontWeight: 600 }}
          title={policy.description}
        >
          {policy.description}
        </Typography>
        <Box
          sx={{ flex: '1 0 auto', textAlign: 'right', whiteSpace: 'nowrap' }}
        >
          {!policy.is_enabled && (
            <Typography variant="caption" color="text.secondary">
              DISABLED
            </Typography>
          )}
        </Box>
        <Tooltip title="Policy details">
          <IconButton
            size="small"
            aria-label={`Policy details for ${policy.description}`}
            onClick={detailsOverlay.open}
            sx={{ flex: 'none' }}
          >
            <InfoOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <ApprovalPolicyDetailsDialog
        overlay={detailsOverlay}
        policy={statePolicy}
        approvers={policyApproverRows(policy)}
        positionNamesById={positionNamesById}
        positionsPending={positionsPending}
        usersById={usersById}
        usersPending={usersPending}
      />
    </Box>
  )
}

type ApprovalPoliciesLinkProps = {
  projectId: string
}

function ApprovalPoliciesLink({ projectId }: ApprovalPoliciesLinkProps) {
  return (
    <Button
      size="small"
      variant="text"
      component="a"
      href={`/projects/${projectId}/settings/approvals`}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
      sx={{ alignSelf: 'flex-start' }}
    >
      View Approval Policies
    </Button>
  )
}

type ApprovalPolicyMatchDisplayProps = {
  action: WorkflowStateAction
}

export default function ApprovalPolicyMatchDisplay({
  action,
}: ApprovalPolicyMatchDisplayProps) {
  const { project } = useProjectContext()
  const policyIds: string[] = action.params.allowed_approval_policy_ids || []

  const { approvalPolicies, isPending } = useGetApprovalPolicies({
    filters: { id__in: policyIds.join(), limit: `${policyIds.length}` },
    enabled: policyIds.length > 0,
  })

  const approverUserIds = approvalPolicies.flatMap((policy) =>
    orderedUserIds(policy.approvers),
  )

  const { usersById, isPending: usersPending } = useUsersById({
    userIds: approverUserIds,
    enabled: approverUserIds.length > 0,
  })

  // A policy's approver list can name a position instead of a person, and the
  // policy only carries its id.
  // Only fetched once a loaded policy actually names a position: the common
  // case is an all-user policy, which needs no catalog at all.
  const namesPosition = approvalPolicies.some((policy) =>
    policy.approvers.some(({ kind }) => kind === 'position'),
  )

  const { positions, isPending: positionsFetching } = useGetPositions({
    filters: { limit: '1000', project_id: project.id },
    enabled: namesPosition,
  })

  const positionsPending = namesPosition && positionsFetching

  const positionNamesById = useMemo(
    () => new Map(positions.map(({ id, name }) => [id, name])),
    [positions],
  )

  if (policyIds.length === 0) {
    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {ALL_POLICIES_TEXT}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {MOVE_ON_TEXT}
          </Typography>
        </Box>
        <ApprovalPoliciesLink projectId={project.id} />
      </Stack>
    )
  }

  const missingCount = policyIds.length - approvalPolicies.length

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {NAMED_POLICIES_TEXT}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {MOVE_ON_TEXT}
        </Typography>
      </Box>
      {isPending ? (
        <Skeleton variant="rounded" height={36} />
      ) : (
        <>
          <Stack spacing={1}>
            {approvalPolicies.map((policy) => (
              <PolicyRow
                key={policy.id}
                policy={policy}
                usersById={usersById}
                positionNamesById={positionNamesById}
                positionsPending={positionsPending}
                usersPending={usersPending}
              />
            ))}
          </Stack>
          {missingCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {missingCount === 1
                ? '1 selected policy no longer exists.'
                : `${missingCount} selected policies no longer exist.`}
            </Typography>
          )}
        </>
      )}
      <ApprovalPoliciesLink projectId={project.id} />
    </Stack>
  )
}
